import { useState, useEffect } from 'react'
import { Loader2, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/hooks/use-toast'
import {
  AdminUser,
  useAvailableCoursesForUser,
  useEnrollUserInCourses,
} from '@/services/admin/users.service'
import { getErrorMessage } from '@/utils/error'

interface EnrollUserDialogProps {
  user: AdminUser
  open: boolean
  onOpenChange: (open: boolean) => void
}

const typeLabels = {
  regular: 'Regular',
  masterclass: 'Masterclass',
  drop: 'Drop',
  workshop: 'Workshop',
  trail: 'Trilha',
}

const levelLabels = {
  beginner: 'Iniciante',
  intermediate: 'Intermediário',
  advanced: 'Avançado',
  expert: 'Expert',
}

const accessTypeLabels = {
  free: 'Gratuito',
  restricted: 'Restrito',
  paid: 'Pago',
}

export function EnrollUserDialog({ user, open, onOpenChange }: EnrollUserDialogProps) {
  const { toast } = useToast()
  const [search, setSearch] = useState('')
  const [selectedCourses, setSelectedCourses] = useState<number[]>([])
  const [showRestrictedOnly, setShowRestrictedOnly] = useState(false)

  const { data: availableCourses, isLoading } = useAvailableCoursesForUser(user.id)
  const enrollMutation = useEnrollUserInCourses()

  useEffect(() => {
    if (!open) {
      setSearch('')
      setSelectedCourses([])
      setShowRestrictedOnly(false)
    }
  }, [open])

  const filteredCourses = availableCourses?.filter((course) => {
    if (showRestrictedOnly && course.accessType !== 'restricted') return false
    if (!search) return true
    return course.title.toLowerCase().includes(search.toLowerCase())
  })

  const handleToggleCourse = (courseId: number) => {
    setSelectedCourses((prev) =>
      prev.includes(courseId) ? prev.filter((id) => id !== courseId) : [...prev, courseId]
    )
  }

  const handleSubmit = async () => {
    if (selectedCourses.length === 0) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'Selecione pelo menos um curso.',
      })
      return
    }

    try {
      await enrollMutation.mutateAsync({
        userId: user.id,
        courseIds: selectedCourses,
      })

      toast({
        title: 'Sucesso!',
        description: `${selectedCourses.length} curso(s) adicionado(s) para ${user.name}.`,
      })

      onOpenChange(false)
    } catch (error: unknown) {
      toast({
        variant: 'destructive',
        title: 'Erro ao matricular',
        description: getErrorMessage(error, 'Ocorreu um erro ao matricular o usuário.'),
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Matricular Usuário em Curso</DialogTitle>
          <DialogDescription>
            <div className="mt-2 space-y-1">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-foreground">👤 {user.name}</span>
                <span className="text-muted-foreground">({user.email})</span>
              </div>
              <div className="text-sm text-muted-foreground">
                📊 Cursos atualmente matriculado:{' '}
                <span className="text-primary font-semibold">{user._count.course_enrollments}</span>
              </div>
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="border-t border-border/50 my-4" />

        <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
          <div>
            <p className="text-sm font-medium mb-3">Selecione o(s) curso(s) para matricular:</p>

            {/* Filtros */}
            <div className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Buscar curso..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="restricted-only"
                  checked={showRestrictedOnly}
                  onCheckedChange={(checked) => setShowRestrictedOnly(checked === true)}
                />
                <label
                  htmlFor="restricted-only"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Mostrar apenas cursos restritos
                </label>
              </div>
            </div>
          </div>

          {/* Lista de cursos */}
          <div className="flex-1 overflow-y-auto border border-border/50 rounded-lg p-4 space-y-3">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : filteredCourses && filteredCourses.length > 0 ? (
              filteredCourses.map((course) => (
                <div
                  key={course.id}
                  className={`border border-border/50 rounded-lg p-3 cursor-pointer transition-colors ${
                    selectedCourses.includes(course.id)
                      ? 'bg-primary/10 border-primary'
                      : 'hover:bg-muted/50'
                  }`}
                  onClick={() => handleToggleCourse(course.id)}
                >
                  <div className="flex items-start gap-3">
                    <Checkbox
                      checked={selectedCourses.includes(course.id)}
                      onCheckedChange={() => handleToggleCourse(course.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium">{course.title}</span>
                        <Badge
                          variant={course.accessType === 'restricted' ? 'default' : 'secondary'}
                        >
                          {accessTypeLabels[course.accessType]}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        ↳ Tipo: {typeLabels[course.type]} | Nível: {levelLabels[course.level]}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <p className="mb-2">ℹ️ Nenhum curso disponível</p>
                <p className="text-sm">
                  {showRestrictedOnly
                    ? 'Não há cursos restritos disponíveis para matrícula'
                    : 'O usuário já está matriculado em todos os cursos disponíveis'}
                </p>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={selectedCourses.length === 0 || enrollMutation.isPending}
          >
            {enrollMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Matriculando...
              </>
            ) : (
              `Matricular (${selectedCourses.length})`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
